import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { i18nLike } from "#web/services/i18nLike";
import { fetchTinybird, parsePipeResult } from "#web/services/tinybird";
import { Badge, Banner, Box, Card, IndexTable, InlineStack, SkeletonBodyText, Text } from "@shopify/polaris";
import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import * as v from "valibot";

const en = {
  error: {
    description: "Please try again. If the issue persists, please contact support.",
    title: "Error loading protected orders",
  },
  headings: {
    blockedCoupon: "Blocked Coupon",
    orderDate: "Order Date",
    orderId: "Order ID",
    recoveredRevenue: "Recovered Revenue",
    totalPrice: "Total Price",
  },
  insufficientData: "Insufficient data. May take a few hours/days to appear",
  paginationLabel: "{{startItem}}-{{endItem}} of {{totalItems}} protected orders",
  protectedOrders: "Protected Orders",
  resourceName: {
    plural: "Protected Orders",
    singular: "Protected Order",
  },
};

const ProtectedOrdersQuery = ({
  endDate,
  shopId,
  startDate,
}: {
  endDate: string;
  shopId: string | undefined;
  startDate: string;
}) =>
  queryOptions({
    enabled: !!shopId,
    placeholderData: keepPreviousData,
    queryFn: () => {
      const ProtectedOrdersData = v.array(
        v.object({
          blocked_coupon: v.optional(v.nullable(v.string())),
          currency: v.string(),
          date: v.string(),
          id: v.number(),
          recovered_revenue: v.optional(v.nullable(v.number())),
          total_price: v.optional(v.nullable(v.number())),
        }),
      );
      return fetchTinybird("protected_orders", { endDate, shopId: shopId!, startDate }).then(
        parsePipeResult(ProtectedOrdersData),
      );
    },
    queryKey: ["protected_orders", shopId, startDate, endDate],
  });

export const ProtectedOrders = ({
  dateRange,
  isCalculated,
}: {
  dateRange: { end: string; start: string };
  isCalculated: boolean;
}) => {
  const t = i18nLike(en);
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;
  const {
    data: protectedOrdersData,
    isError: protectedOrdersError,
    isFetching: protectedOrdersFetching,
    isPending: protectedOrdersPending,
  } = useQuery(ProtectedOrdersQuery({ endDate: dateRange.end, shopId: shop?.id, startDate: dateRange.start }));

  const isError = shopError || protectedOrdersError;
  const isInitialLoading = shopFetching || protectedOrdersPending;

  if (isError) {
    return (
      <Banner title={t("error.title")} tone="critical">
        {t("error.description")}
      </Banner>
    );
  }

  if (isCalculated) {
    return (
      <Card padding="0">
        <Box background="bg-surface-secondary" padding="400">
          <Text as="h2" variant="headingMd">
            {t("protectedOrders")}
          </Text>
        </Box>
        <InlineStack align="center">
          <Box padding="800">
            <Badge>{t("insufficientData")}</Badge>
          </Box>
        </InlineStack>
      </Card>
    );
  }

  return (
    <Card padding="0">
      <Box background="bg-surface-secondary" padding="400">
        <Text as="h2" variant="headingMd">
          {t("protectedOrders")}
        </Text>
      </Box>
      {isInitialLoading ? (
        <Box padding="400">
          <SkeletonBodyText />
        </Box>
      ) : (
        <IndexTable
          emptyState={
            <InlineStack align="center">
              <Box padding="400">
                <Badge>{t("insufficientData")}</Badge>
              </Box>
            </InlineStack>
          }
          headings={[
            { title: t("headings.orderId") },
            { title: t("headings.recoveredRevenue") },
            { title: t("headings.blockedCoupon") },
            { title: t("headings.orderDate") },
            { title: t("headings.totalPrice") },
          ]}
          itemCount={protectedOrdersData.data.length || 0}
          loading={protectedOrdersFetching}
          resourceName={{
            plural: t("resourceName.plural"),
            singular: t("resourceName.singular"),
          }}
          selectable={false}
        >
          {protectedOrdersData.data.map((item) => (
            <IndexTable.Row id={item.id.toString()} key={item.id.toString()} position={item.id}>
              <IndexTable.Cell>{item.id}</IndexTable.Cell>

              <IndexTable.Cell>
                {item.recovered_revenue}
                <span style={{ fontSize: "0.75rem" }}> {item.currency}</span>
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Badge tone="magic">{item.blocked_coupon ?? ""}</Badge>
              </IndexTable.Cell>
              <IndexTable.Cell>{item.date}</IndexTable.Cell>
              <IndexTable.Cell>
                {item.total_price} <span style={{ fontSize: "0.75rem" }}> {item.currency}</span>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      )}
    </Card>
  );
};
